/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { DOCUMENT } from "@angular/common";
import { HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { defer, Observable, tap } from "rxjs";
import {
	IctCarbonInsightsReportParams,
	IctCarbonInsightsReportTab
} from "../models/http/dashboard/ict-carbon-insights-report.model";
import { HttpMethods } from "../models/ui/http-methods.model";
import { ENDPOINT_GROUPS } from "../utils/apis/apis.constants";
import { saveAs } from "../utils/utils";
import { CommunicationService } from "./communication.service";
import { ToastService } from "./toast.service";

@Injectable({
	providedIn: "root"
})
export class IctCarbonInsightsReportService {
	private readonly _document = inject(DOCUMENT);
	private readonly _communicationService = inject(CommunicationService);
	private readonly _toastService = inject(ToastService);
	private readonly _operation =
		ENDPOINT_GROUPS.dashboardSoftwareAssets.operations.exportInsightsReport;

	public downloadReport(
		tab: IctCarbonInsightsReportTab,
		reportParams: IctCarbonInsightsReportParams
	): Observable<Blob> {
		const params = new HttpParams().set("tab", tab);

		return defer(() => {
			this._toastService.success(
				"Download started",
				"Your report download has started successfully. Please wait patiently while the file is being prepared."
			);
			return this._communicationService.callService(
				this._operation.service,
				HttpMethods.POST,
				{
					params,
					pathParams: { format: "pdf" },
					responseType: "blob"
				},
				reportParams
			);
		}).pipe(
			tap((pdfBlob: Blob) => {
				const url = URL.createObjectURL(pdfBlob);
				saveAs(this._document, url, `ict-carbon-insights-${tab}-report.pdf`);
				URL.revokeObjectURL(url);
			})
		);
	}
}
